import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Button,
  Autocomplete,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import API from "../../../Services/API";
import {
  ExportPurchaseVouchersExcel,
  GetApprovedSuppliers,
  GetPurchaseOrdersBySupplier,
  GetPurchaseVoucherByPo,
} from "../../../Services/Services";
import { generatePurchasePdf } from "../Accounts/PurchaseVoucherPrint";
import InfiniteScroll from "react-infinite-scroll-component";

// ===== Debounce Utility =====
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ===== Main Component =====
function PurchaseVoucherPo() {
  // ===== States =====
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseVouchers, setPurchaseVouchers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [posLoading, setPosLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  // ===== Debounced Fetch Suppliers =====
  const debouncedFetchSuppliers = useCallback(
    debounce((searchTerm) => {
      fetchSuppliers(searchTerm);
    }, 500),
    [],
  );

  // ===== Fetch Suppliers on Mount =====
  useEffect(() => {
    fetchSuppliers("");
  }, []);

  // ===== Fetch POs when Supplier Changes =====
  useEffect(() => {
    if (selectedSupplier) {
      fetchPurchaseOrders(selectedSupplier.id);
    } else {
      setPurchaseOrders([]);
      setSelectedPO(null);
      setPurchaseVouchers([]);
    }
  }, [selectedSupplier]);

  // ===== Fetch Vouchers when PO is Selected =====
  useEffect(() => {
    if (selectedPO && selectedSupplier) {
      setPage(1);
      setHasMore(true);
      setPurchaseVouchers([]);
      fetchPurchaseVouchers(selectedSupplier.id, selectedPO.po_id, 1, false);
    } else {
      setPurchaseVouchers([]);
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
    }
  }, [selectedPO, selectedSupplier]);

  // ===== API Functions =====
  const fetchSuppliers = async (searchTerm = "") => {
    setSuppliersLoading(true);
    try {
      const data = await GetApprovedSuppliers(1, { name: searchTerm });
      const suppliersList = data.results || data || [];
      setSuppliers(Array.isArray(suppliersList) ? suppliersList : []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to load suppliers");
      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const fetchPurchaseOrders = async (supplierId) => {
    setPosLoading(true);
    setSelectedPO(null);
    setPurchaseVouchers([]);
    try {
      const data = await GetPurchaseOrdersBySupplier(supplierId);
      const poList = data.results || data || [];
      setPurchaseOrders(Array.isArray(poList) ? poList : []);

      if (poList.length === 0) {
        toast.info(
          "No approved purchase orders found for this supplier in current division",
        );
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      toast.error("Failed to load purchase orders");
      setPurchaseOrders([]);
    } finally {
      setPosLoading(false);
    }
  };

  const fetchPurchaseVouchers = async (
    supplierId,
    poId,
    pageNum = 1,
    append = false,
  ) => {
    if (pageNum === 1) {
      setLoading(true);
    }

    try {
      const data = await GetPurchaseVoucherByPo({
        supplier: supplierId,
        purchase_order: poId,
        page: pageNum,
      });

      const voucherList = data.results || data || [];
      const count = data.count || voucherList.length;

      if (append) {
        setPurchaseVouchers((prev) => [...prev, ...voucherList]);
      } else {
        setPurchaseVouchers(Array.isArray(voucherList) ? voucherList : []);
      }

      setTotalCount(count);
      setHasMore(data.next !== null);
      setPage(pageNum);

      if (pageNum === 1 && voucherList.length === 0) {
        toast.info("No purchase vouchers found for this PO");
      } else if (pageNum === 1) {
        toast.success(`Found ${count} purchase voucher(s)`);
      }
    } catch (error) {
      console.error("Error fetching purchase vouchers:", error);
      toast.error("Failed to load purchase vouchers");
      if (!append) {
        setPurchaseVouchers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (selectedSupplier && selectedPO && hasMore) {
      fetchPurchaseVouchers(
        selectedSupplier.id,
        selectedPO.po_id,
        page + 1,
        true,
      );
    }
  };

  // ===== Handler Functions =====
  const handleReset = () => {
    setSelectedSupplier(null);
    setSelectedPO(null);
    setPurchaseVouchers([]);
    setPurchaseOrders([]);
    setSupplierSearch("");
    setExpandedItem(null);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);
    toast.info("Filters reset");
  };

  const handleExcelExport = async () => {
    if (!selectedSupplier || !selectedPO) {
      toast.warning("Please select Supplier and PO Number first");
      return;
    }

    setExcelLoading(true);
    try {
      const filters = {
        supplier: selectedSupplier.id,
        purchase_order: selectedPO.po_id,
      };

      const blob = await ExportPurchaseVouchersExcel(filters);

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Purchase_Vouchers_${selectedPO.po_number || "export"}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully!");
      } else {
        toast.error("Failed to export Excel");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setExcelLoading(false);
    }
  };

  // ===== Helper Functions =====
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("approved") || statusLower.includes("complete")) {
      return "#4caf50";
    } else if (statusLower.includes("pending")) {
      return "#ff9800";
    } else if (
      statusLower.includes("rejected") ||
      statusLower.includes("cancel")
    ) {
      return "#f44336";
    } else if (
      statusLower.includes("partial") ||
      statusLower.includes("draft")
    ) {
      return "#2196f3";
    }
    return "#9e9e9e";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return `₹${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Calculate totals
  const totalVouchers = purchaseVouchers.length;
  const totalAmount = purchaseVouchers.reduce(
    (sum, voucher) => sum + (Number(voucher.final_amount) || 0),
    0,
  );
  const totalPaidAmount = purchaseVouchers.reduce(
    (sum, voucher) => sum + (Number(voucher.receipt_amount) || 0),
    0,
  );
  const totalBalanceAmount = totalAmount - totalPaidAmount;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {/* Filter Section */}
      <Box sx={{ mb: 2, mt: isMobile ? 2 : -5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Supplier Autocomplete */}
          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name || ""}
              value={selectedSupplier}
              onChange={(event, newValue) => {
                setSelectedSupplier(newValue);
              }}
              onInputChange={(e, newInputValue) => {
                setSupplierSearch(newInputValue);
                debouncedFetchSuppliers(newInputValue);
              }}
              loading={suppliersLoading}
              size="small"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier *"
                  placeholder="Search and select supplier"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {suppliersLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
          </Grid>

          {/* Purchase Order Autocomplete */}
          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              options={purchaseOrders}
              getOptionLabel={(option) => option.po_number || ""}
              value={selectedPO}
              onChange={(event, newValue) => {
                setSelectedPO(newValue);
              }}
              loading={posLoading}
              size="small"
              isOptionEqualToValue={(option, value) =>
                option.po_id === value?.po_id
              }
              renderOption={(props, option) => (
                <li {...props} key={option.po_id}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      {option.po_number}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {formatDate(option.created_at || option.po_date)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PO Number *"
                  placeholder={
                    selectedSupplier
                      ? "Select PO number"
                      : "Select supplier first"
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {posLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
          </Grid>

          {/* Reset Button */}
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export Button */}
          <Grid item xs={6} sm={1}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleExcelExport}
                  sx={{
                    borderRadius: "4px",
                    padding: "6px",
                  }}
                >
                  {excelLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading purchase vouchers...
          </Typography>
        </Box>
      )}

      {/* No Data Message */}
      {!loading && purchaseVouchers.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "#fafafa",
            borderRadius: 2,
            border: "2px dashed #ccc",
          }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            {!selectedSupplier
              ? "🏢 Please select a Supplier"
              : !selectedPO
                ? "📋 Please select a PO Number"
                : "📭 No Purchase Vouchers Found"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {!selectedSupplier
              ? "Choose a supplier from the dropdown to view purchase orders"
              : !selectedPO
                ? "Choose a PO number to view the purchase vouchers"
                : "No purchase vouchers found for the selected PO"}
          </Typography>
        </Box>
      )}

      {/* Data Display - Mobile vs Desktop */}
      {!loading && purchaseVouchers.length > 0 && (
        <>
          {isMobile ? (
            /* 📱 MOBILE VIEW */
            <Box
              id="scrollableMobileContainer"
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                maxHeight: "65vh",
                overflowY: "auto",
              }}
            >
              <InfiniteScroll
                dataLength={purchaseVouchers.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Loading more...
                    </Typography>
                  </Box>
                }
                endMessage={
                  purchaseVouchers.length > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 1 }}
                    >
                      No more vouchers
                    </Typography>
                  )
                }
                scrollableTarget="scrollableMobileContainer"
              >
                {purchaseVouchers.map((voucher, index) => (
                  <Box key={voucher.id || index}>
                    {/* Header Card */}
                    <Box
                      onClick={() => {
                        setExpandedItem(expandedItem === index ? null : index);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        cursor: "pointer",
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:active": { bgcolor: "#e3f2fd" },
                        borderLeft: `4px solid ${getStatusColor(voucher.status)}`,
                        transition: "all 0.2s",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              minWidth: 30,
                              fontWeight: 600,
                            }}
                          >
                            #{index + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              flex: 1,
                              color: "#1976d2",
                            }}
                          >
                            {voucher.voucher_no || "-"}
                          </Typography>
                          <Chip
                            label={voucher.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(voucher.status),
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              height: 22,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            {new Date(
                              voucher.voucher_date || voucher.created_at,
                            ).toLocaleDateString("en-GB")}
                          </span>
                          <span style={{ color: "#999" }}>•</span>
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: "#2e7d32",
                              fontWeight: 700,
                            }}
                          >
                            {formatCurrency(voucher.total_amount)}
                          </span>
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ ml: 1 }}>
                        {expandedItem === index ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    {/* Collapsible Details */}
                    <Collapse
                      in={expandedItem === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 2,
                          bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                          borderLeft: `4px solid ${getStatusColor(
                            voucher.status,
                          )}`,
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Voucher Number
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: "#1976d2",
                              }}
                            >
                              {voucher.purchase_number || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Total Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#2e7d32",
                              }}
                            >
                              {formatCurrency(voucher.total_amount)}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Paid Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#1976d2",
                              }}
                            >
                              {formatCurrency(voucher.receipt_amount || "-")}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Balance Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#ff9800",
                              }}
                            >
                              {formatCurrency(
                                voucher.balance_amount ||
                                  (voucher.total_amount || 0) -
                                    (voucher.paid_amount || 0),
                              )}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Payment Status
                            </Typography>
                            <Chip
                              label={voucher.payment_status || "Pending"}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(voucher.payment_status),
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                height: 22,
                                mt: 0.5,
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Project
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "#333",
                              }}
                            >
                              {voucher.project_name || voucher.project || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              LR No
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.lr_no || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Vehicle No
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.vehicle_no || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              Created By
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.created_by ||
                                voucher.created_by_name ||
                                "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                    <Divider />
                  </Box>
                ))}
              </InfiniteScroll>

              {/* Mobile Total Footer */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#e8f5e9",
                  borderTop: "3px solid #4caf50",
                  position: "sticky",
                  bottom: 0,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Vouchers
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "#1976d2",
                        }}
                      >
                        {purchaseVouchers.length}/{totalCount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Total Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          color: "#2e7d32",
                        }}
                      >
                        {formatCurrency(totalAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Balance
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          color: "#ff9800",
                        }}
                      >
                        {formatCurrency(totalBalanceAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW */
            <TableContainer
              component={Paper}
              id="scrollableTableContainer"
              sx={{
                maxHeight: "65vh",
                overflowX: { xs: "auto", sm: "unset" },
                overflowY: "auto",
                width: "100%",
              }}
            >
              <InfiniteScroll
                dataLength={purchaseVouchers.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Loading more vouchers...
                    </Typography>
                  </Box>
                }
                endMessage={
                  purchaseVouchers.length > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No more vouchers to load
                    </Typography>
                  )
                }
                scrollableTarget="scrollableTableContainer"
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: "max-content",
                    "& td, & th": {
                      borderBottom: "1px solid #e0e0e0",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      px: 1.5,
                      py: 1,
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        margin: 0,
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#2196f3" : "#333",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      },
                    }}
                  >
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          color: "#333",
                          backgroundColor: "#f5f5f5",
                          borderBottom: "2px solid #1976d2",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#f5f5f5 !important",
                          zIndex: 3,
                          minWidth: 60,
                        }}
                      >
                        S.No.
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Voucher No</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Total Amount</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Paid Amount</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        Balance Amount
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Project</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        Purchase Order
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>LR No</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Vehicle No</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        Payment Status
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Date</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Created By</TableCell>
                      <TableCell
                        sx={{
                          minWidth: 80,
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#f5f5f5 !important",
                          zIndex: 3,
                          borderLeft: "2px solid #e0e0e0",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseVouchers.map((voucher, index) => (
                      <TableRow
                        hover
                        key={voucher.id || index}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:hover": {
                            backgroundColor: "#e3f2fd !important",
                            "& td": { backgroundColor: "#e3f2fd !important" },
                          },
                          "& td": {
                            paddingRight: "13px !important",
                            paddingLeft: "13px !important",
                            padding: "0px",
                            fontSize: { xs: "0.65rem", sm: "0.8rem" },
                          },
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor:
                              index % 2 === 0
                                ? "#fafafa !important"
                                : "#ffffff !important",
                            zIndex: 2,
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#1976d2",
                          }}
                        >
                          {voucher.purchase_number || "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.status || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(voucher.status),
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#2e7d32",
                          }}
                        >
                          {formatCurrency(voucher.final_amount)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#1976d2",
                          }}
                        >
                          {formatCurrency(voucher.receipt_amount)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#ff9800",
                          }}
                        >
                          {formatCurrency(
                            voucher.balance_amount ||
                              (voucher.final_amount || 0) -
                                (voucher.receipt_amount || 0),
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Tooltip
                            title={
                              voucher.project_name || voucher.project || "-"
                            }
                            arrow
                          >
                            <span>
                              {voucher.project_name || voucher.project || "-"}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            color: "#1976d2",
                          }}
                        >
                          {voucher.po_number || voucher.purchase_order || "-"}
                        </TableCell>
                        <TableCell>{voucher.lr_no || "-"}</TableCell>
                        <TableCell>{voucher.vehicle_no || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.payment_status || "Pending"}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(voucher.payment_status),
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(
                            voucher.voucher_date || voucher.created_at,
                          ).toLocaleDateString("en-GB")}
                        </TableCell>
                        <TableCell>
                          {voucher.created_by_name || voucher.created_by || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            backgroundColor:
                              index % 2 === 0
                                ? "#fafafa !important"
                                : "#ffffff !important",
                            zIndex: 2,
                            borderLeft: "2px solid #e0e0e0",
                          }}
                        >
                          <Tooltip title="Print Voucher" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => generatePurchasePdf(voucher)}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#e3f2fd",
                                },
                              }}
                            >
                              <PrintIcon transform="scale(0.7)" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Total Row */}
                    <TableRow
                      sx={{
                        backgroundColor: "#e8f5e9 !important",
                        "& td": {
                          fontWeight: "700 !important",
                          fontSize: "0.85rem !important",
                          borderTop: "2px solid #4caf50",
                          padding: 0.5,
                        },
                      }}
                    >
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#e8f5e9 !important",
                          zIndex: 2,
                        }}
                      >
                        TOTAL ({totalVouchers} Vouchers)
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ color: "#2e7d32" }}>
                        {formatCurrency(totalAmount)}
                      </TableCell>
                      <TableCell sx={{ color: "#1976d2" }}>
                        {formatCurrency(totalPaidAmount)}
                      </TableCell>
                      <TableCell sx={{ color: "#ff9800" }}>
                        {formatCurrency(totalBalanceAmount)}
                      </TableCell>
                      <TableCell colSpan={7}></TableCell>
                      {/* Sticky Action cell for total row */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#e8f5e9 !important",
                          zIndex: 2,
                          borderLeft: "2px solid #e0e0e0",
                        }}
                      ></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

export default PurchaseVoucherPo;